import React, { useState, useCallback, useRef } from 'react';
import { Upload, X, Trash2, GripVertical, Loader2, CheckCircle, AlertCircle, Crop as CropIcon } from 'lucide-react';
import { toast } from 'sonner';
import ImageCropModal from './ImageCropModal';
import { resolveImageUrl } from '../../lib/imageUrl';

const API_URL = '';

const ACCEPT_MAP = {
  products: '.jpg,.jpeg,.png,.webp',
  categories: '.jpg,.jpeg,.png,.webp',
  logo: '.png,.svg,.jpg,.jpeg',
  banners: '.jpg,.jpeg,.png,.webp',
};

const MAX_SIZE_MAP = {
  products: 5 * 1024 * 1024,
  categories: 5 * 1024 * 1024,
  logo: 2 * 1024 * 1024,
  banners: 5 * 1024 * 1024,
};

// Helper to upload a single file to the backend
async function uploadFileToServer(file, uploadType, onProgress) {
  const formData = new FormData();
  formData.append('file', file);
  formData.append('upload_type', uploadType);

  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable && onProgress) {
        onProgress(Math.round((e.loaded / e.total) * 90));
      }
    };
    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        resolve(JSON.parse(xhr.responseText));
      } else {
        try {
          const err = JSON.parse(xhr.responseText);
          reject(new Error(err.detail || 'Erreur upload'));
        } catch {
          reject(new Error('Erreur upload'));
        }
      }
    };
    xhr.onerror = () => reject(new Error('Erreur réseau'));
    xhr.open('POST', `${API_URL}/api/bloom-gestion-secure/upload`);
    xhr.withCredentials = true;
    xhr.send(formData);
  });
}

function validateFile(file, uploadType) {
  const maxSize = MAX_SIZE_MAP[uploadType] || 5 * 1024 * 1024;
  if (file.size > maxSize) {
    return `Fichier trop volumineux (max ${maxSize / 1024 / 1024}MB)`;
  }
  const ext = file.name.split('.').pop()?.toLowerCase();
  const allowed = (ACCEPT_MAP[uploadType] || '').replace(/\./g, '').split(',');
  if (!allowed.includes(ext)) {
    return `Format non autorisé. Formats acceptés: ${allowed.join(', ').toUpperCase()}`;
  }
  return null;
}

// Single image upload zone with crop support
export function ImageUploadZone({ onUploaded, uploadType = 'products', currentImage = null, label = "Déposez votre image ici ou cliquez pour parcourir", compact = false, enableCrop = true }) {
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [dragOver, setDragOver] = useState(false);
  const [cropData, setCropData] = useState(null); // { src, fileName }
  const inputRef = useRef(null);

  const doUpload = useCallback(async (file) => {
    setUploading(true);
    setProgress(10);
    try {
      const result = await uploadFileToServer(file, uploadType, setProgress);
      setProgress(100);
      toast.success('Image uploadée');
      onUploaded(result);
    } catch (err) {
      toast.error(err.message || "Erreur lors de l'upload");
    } finally {
      setTimeout(() => { setUploading(false); setProgress(0); }, 500);
    }
  }, [uploadType, onUploaded]);

  const handleFile = useCallback((file) => {
    const err = validateFile(file, uploadType);
    if (err) { toast.error(err); return; }

    const isSvg = file.name.toLowerCase().endsWith('.svg');
    if (enableCrop && !isSvg) {
      const reader = new FileReader();
      reader.onload = () => setCropData({ src: reader.result, fileName: file.name, originalFile: file });
      reader.readAsDataURL(file);
    } else {
      doUpload(file);
    }
  }, [uploadType, enableCrop, doUpload]);

  const onCropDone = useCallback((croppedFile) => {
    setCropData(null);
    doUpload(croppedFile);
  }, [doUpload]);

  const onDrop = useCallback((e) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer?.files?.[0];
    if (file) handleFile(file);
  }, [handleFile]);

  const onFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (file) handleFile(file);
    e.target.value = '';
  };

  return (
    <>
      {cropData && (
        <ImageCropModal
          imageSrc={cropData.src}
          fileName={cropData.fileName}
          onCropDone={onCropDone}
          onCancel={() => {
            setCropData(null);
            // Upload without cropping
            doUpload(cropData.originalFile);
          }}
        />
      )}

      {compact ? (
        <div className="relative">
          <div
            className={`relative border-2 border-dashed rounded transition-colors cursor-pointer overflow-hidden
              ${dragOver ? 'border-[#D4AF37] bg-[#D4AF37]/5' : 'border-[#E0DFDD] hover:border-[#D4AF37]'}
              w-full aspect-[3/4]`}
            onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
            onDragLeave={() => setDragOver(false)}
            onDrop={onDrop}
            onClick={() => inputRef.current?.click()}
            data-testid="upload-zone-compact"
          >
            {currentImage ? (
              <img src={currentImage} alt="" className="w-full h-full object-cover" />
            ) : (
              <div className="flex flex-col items-center justify-center h-full p-2 text-center">
                <Upload size={20} className="text-[#6B6A68] mb-1" />
                <span className="text-[10px] text-[#6B6A68]">Cliquez ou déposez</span>
              </div>
            )}
            {uploading && (
              <div className="absolute inset-0 bg-black/50 flex flex-col items-center justify-center">
                <Loader2 size={20} className="animate-spin text-white mb-1" />
                <div className="w-3/4 h-1.5 bg-white/30 rounded overflow-hidden">
                  <div className="h-full bg-[#D4AF37] transition-all" style={{ width: `${progress}%` }} />
                </div>
              </div>
            )}
          </div>
          <input ref={inputRef} type="file" accept={ACCEPT_MAP[uploadType]} onChange={onFileSelect} className="hidden" />
        </div>
      ) : (
        <div>
          <div
            className={`border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-colors
              ${dragOver ? 'border-[#D4AF37] bg-[#D4AF37]/5' : 'border-[#E0DFDD] hover:border-[#D4AF37]'}`}
            onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
            onDragLeave={() => setDragOver(false)}
            onDrop={onDrop}
            onClick={() => inputRef.current?.click()}
            data-testid="upload-zone"
          >
            {uploading ? (
              <div className="py-2">
                <Loader2 size={28} className="animate-spin text-[#D4AF37] mx-auto mb-3" />
                <div className="w-full max-w-xs mx-auto h-2 bg-[#F5F4F2] rounded overflow-hidden">
                  <div className="h-full bg-[#D4AF37] transition-all duration-300" style={{ width: `${progress}%` }} />
                </div>
                <p className="text-xs text-[#6B6A68] mt-2">{progress}%</p>
              </div>
            ) : (
              <>
                <div className="flex items-center justify-center gap-2 mb-3">
                  <Upload size={24} className="text-[#6B6A68]" />
                  {enableCrop && <CropIcon size={18} className="text-[#D4AF37]" />}
                </div>
                <p className="text-sm text-[#6B6A68]">{label}</p>
                <p className="text-xs text-[#9E9C9A] mt-1">
                  {uploadType === 'logo' ? 'PNG, SVG, JPG — Max 2MB' : 'JPG, PNG, WEBP — Max 5MB'}
                  {enableCrop && ' — Recadrage intégré'}
                </p>
              </>
            )}
          </div>
          <input ref={inputRef} type="file" accept={ACCEPT_MAP[uploadType]} onChange={onFileSelect} className="hidden" />
        </div>
      )}
    </>
  );
}


// Multi-image upload with reorder + delete + crop (for products)
export function MultiImageUploader({ images = [], onChange, uploadType = 'products', maxImages = 10 }) {
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [dragOverIndex, setDragOverIndex] = useState(null);
  const [dragStartIndex, setDragStartIndex] = useState(null);
  const [cropQueue, setCropQueue] = useState([]); // [{src, fileName, originalFile}]
  const [currentCropIndex, setCurrentCropIndex] = useState(-1);
  const [filesToUpload, setFilesToUpload] = useState([]); // Files ready to upload after crop
  const inputRef = useRef(null);

  // Process files: show crop modal for each one
  const handleFiles = useCallback((fileList) => {
    const files = Array.from(fileList);
    if (images.length + files.length > maxImages) {
      toast.error(`Maximum ${maxImages} images par produit`);
      return;
    }

    // Validate all files first
    const validFiles = [];
    for (const file of files) {
      const err = validateFile(file, uploadType);
      if (err) { toast.error(`${file.name}: ${err}`); continue; }
      validFiles.push(file);
    }

    if (validFiles.length === 0) return;

    // Build crop queue
    const queue = validFiles.map(file => {
      const reader = new FileReader();
      return new Promise((resolve) => {
        reader.onload = () => resolve({ src: reader.result, fileName: file.name, originalFile: file });
        reader.readAsDataURL(file);
      });
    });

    Promise.all(queue).then(items => {
      setCropQueue(items);
      setCurrentCropIndex(0);
      setFilesToUpload([]);
    });
  }, [images.length, maxImages, uploadType]);

  // When crop is done for one image
  const onCropDone = useCallback((croppedFile) => {
    setFilesToUpload(prev => [...prev, croppedFile]);
    if (currentCropIndex < cropQueue.length - 1) {
      setCurrentCropIndex(prev => prev + 1);
    } else {
      // All cropped, now upload all
      setCropQueue([]);
      setCurrentCropIndex(-1);
      uploadBatch([...filesToUpload, croppedFile]);
    }
  }, [currentCropIndex, cropQueue.length, filesToUpload]);

  // Skip crop for current image (upload original)
  const onCropSkip = useCallback(() => {
    const original = cropQueue[currentCropIndex]?.originalFile;
    if (original) {
      setFilesToUpload(prev => [...prev, original]);
    }
    if (currentCropIndex < cropQueue.length - 1) {
      setCurrentCropIndex(prev => prev + 1);
    } else {
      setCropQueue([]);
      setCurrentCropIndex(-1);
      uploadBatch([...filesToUpload, original].filter(Boolean));
    }
  }, [currentCropIndex, cropQueue, filesToUpload]);

  // Upload batch of files
  const uploadBatch = useCallback(async (files) => {
    if (!files.length) return;
    setUploading(true);
    const newImages = [...images];
    let uploaded = 0;

    for (const file of files) {
      try {
        const result = await uploadFileToServer(file, uploadType, (p) => {
          const fileProgress = (uploaded / files.length) * 100 + (p / files.length);
          setProgress(Math.round(fileProgress));
        });

        newImages.push({
          url: result.url,
          thumbnail_url: result.thumbnail_url || null,
          file_id: result.file_id,
          alt: file.name.replace(/\.[^.]+$/, ''),
        });
        uploaded++;
      } catch (err) {
        toast.error(`${file.name}: ${err.message}`);
      }
    }

    setProgress(100);
    onChange(newImages);
    if (uploaded > 0) toast.success(`${uploaded} image${uploaded > 1 ? 's' : ''} uploadée${uploaded > 1 ? 's' : ''}`);
    setTimeout(() => { setUploading(false); setProgress(0); }, 500);
  }, [images, onChange, uploadType]);

  const onDrop = useCallback((e) => {
    e.preventDefault();
    // Reorder logic
    if (dragStartIndex !== null) {
      const dropIndex = dragOverIndex;
      if (dropIndex !== null && dropIndex !== dragStartIndex) {
        const reordered = [...images];
        const [moved] = reordered.splice(dragStartIndex, 1);
        reordered.splice(dropIndex, 0, moved);
        onChange(reordered);
      }
      setDragStartIndex(null);
      setDragOverIndex(null);
      return;
    }
    // File drop
    const files = e.dataTransfer?.files;
    if (files?.length) handleFiles(files);
  }, [dragStartIndex, dragOverIndex, images, onChange, handleFiles]);

  const removeImage = (index) => {
    onChange(images.filter((_, i) => i !== index));
  };

  const handleReorderStart = (e, index) => {
    e.dataTransfer.effectAllowed = 'move';
    setDragStartIndex(index);
  };

  // Crop modal for current image in queue
  const currentCrop = cropQueue[currentCropIndex];

  return (
    <div data-testid="multi-image-uploader">
      {/* Crop modal */}
      {currentCrop && (
        <ImageCropModal
          imageSrc={currentCrop.src}
          fileName={currentCrop.fileName}
          onCropDone={onCropDone}
          onCancel={onCropSkip}
        />
      )}

      {/* Upload zone */}
      <div
        className={`border-2 border-dashed rounded-lg p-6 text-center cursor-pointer transition-colors mb-4
          ${uploading ? 'border-[#D4AF37] bg-[#D4AF37]/5' : 'border-[#E0DFDD] hover:border-[#D4AF37]'}`}
        onDragOver={(e) => { e.preventDefault(); }}
        onDrop={(e) => { e.preventDefault(); handleFiles(e.dataTransfer?.files); }}
        onClick={() => !uploading && inputRef.current?.click()}
        data-testid="multi-upload-dropzone"
      >
        {uploading ? (
          <div className="py-2">
            <Loader2 size={24} className="animate-spin text-[#D4AF37] mx-auto mb-2" />
            <div className="w-full max-w-xs mx-auto h-2 bg-[#F5F4F2] rounded overflow-hidden">
              <div className="h-full bg-[#D4AF37] transition-all duration-300" style={{ width: `${progress}%` }} />
            </div>
            <p className="text-xs text-[#6B6A68] mt-2">Upload en cours... {progress}%</p>
          </div>
        ) : (
          <>
            <div className="flex items-center justify-center gap-2 mb-2">
              <Upload size={24} className="text-[#6B6A68]" />
              <CropIcon size={18} className="text-[#D4AF37]" />
            </div>
            <p className="text-sm text-[#6B6A68]">Déposez vos photos ici ou cliquez pour parcourir</p>
            <p className="text-xs text-[#9E9C9A] mt-1">JPG, PNG, WEBP — Max 5MB — {images.length}/{maxImages} images — Recadrage intégré</p>
          </>
        )}
      </div>
      <input
        ref={inputRef}
        type="file"
        accept={ACCEPT_MAP[uploadType]}
        multiple
        onChange={(e) => { handleFiles(e.target.files); e.target.value = ''; }}
        className="hidden"
        data-testid="multi-upload-input"
      />

      {/* Image grid with reorder */}
      {images.length > 0 && (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-3" data-testid="image-grid">
          {images.map((img, index) => (
            <div
              key={img.file_id || img.url || index}
              className={`relative group aspect-[3/4] bg-[#F5F4F2] overflow-hidden border-2 transition-colors
                ${index === 0 ? 'border-[#D4AF37]' : 'border-transparent'}
                ${dragOverIndex === index ? 'border-[#D4AF37] scale-105' : ''}`}
              draggable
              onDragStart={(e) => handleReorderStart(e, index)}
              onDragOver={(e) => { e.preventDefault(); setDragOverIndex(index); }}
              onDragLeave={() => setDragOverIndex(null)}
              onDrop={onDrop}
              data-testid={`product-image-${index}`}
            >
              <img
                src={resolveImageUrl(img.thumbnail_url || img.url)}
                alt={img.alt || ''}
                className="w-full h-full object-cover"
                onError={(e) => { e.target.src = resolveImageUrl(img.url); }}
              />
              {index === 0 && (
                <div className="absolute top-1 left-1 bg-[#D4AF37] text-[#1A1514] text-[9px] font-bold px-1.5 py-0.5 uppercase">
                  Principale
                </div>
              )}
              <div className="absolute inset-0 bg-black/0 group-hover:bg-black/40 transition-colors flex items-center justify-center opacity-0 group-hover:opacity-100">
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); removeImage(index); }}
                    className="p-2 bg-red-600 text-white rounded-full hover:bg-red-700"
                    data-testid={`delete-image-${index}`}
                  >
                    <Trash2 size={14} />
                  </button>
                  <div className="p-2 bg-white/80 text-[#1A1514] rounded-full cursor-grab">
                    <GripVertical size={14} />
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {images.length > 0 && (
        <p className="text-xs text-[#6B6A68] mt-2">
          Glissez-déposez pour réorganiser. La première image est la photo principale du produit.
        </p>
      )}
    </div>
  );
}
